import { useEffect, useState } from 'react';
import { KeyRound, CheckCircle2, Globe, Mail, Clock, RefreshCw, Save, AlertTriangle } from 'lucide-react';
import { changePassword, apiFetch } from '../lib/api';

const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

type SiteSettings = Record<string, string>;

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="sm:w-56 shrink-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{label}</p>
        {description && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 leading-snug">{description}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  );
}

export default function Settings() {
  // Password section
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState(false);

  // Site settings
  const [settings, setSettings] = useState<SiteSettings>({});
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const loadSettings = async () => {
    setLoadingSettings(true);
    const res = await apiFetch('/api/admin/site-settings');
    if (res.ok) { setSettings(await res.json()); }
    setLoadingSettings(false);
  };

  useEffect(() => { loadSettings(); }, []);

  const set = (key: string, value: string) => {
    setSettings(s => ({ ...s, [key]: value }));
    setDirty(true);
    setSettingsSaved(false);
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    setSettingsError(null);
    const res = await apiFetch('/api/admin/site-settings', { method: 'PUT', body: JSON.stringify(settings) });
    if (res.ok) { setSettingsSaved(true); setDirty(false); } 
    else { setSettingsError('Failed to save settings.'); }
    setSettingsSaving(false);
  };

  const submitPwd = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null); setPwdSuccess(false);
    if (newPwd.length < 8) { setPwdError('At least 8 characters.'); return; }
    if (newPwd !== confirmPwd) { setPwdError('Passwords do not match.'); return; }
    setPwdSaving(true);
    try {
      await changePassword(currentPwd, newPwd);
      setPwdSuccess(true); setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) { setPwdError(err instanceof Error ? err.message : 'Failed'); }
    setPwdSaving(false);
  };

  const bool = (key: string) => settings[key] === 'true';

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 dark:from-gray-200 dark:to-gray-100 bg-clip-text text-transparent mb-1">Settings</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">Site configuration, email settings, and account management.</p>

      {/* ── Site Settings ─────────────────────────────── */}
      <section className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={18} className="text-blue-500" />
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">Site Settings</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadSettings} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <RefreshCw size={14} className={loadingSettings ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={saveSettings}
              disabled={settingsSaving || !dirty}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
            >
              <Save size={14} /> {settingsSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        {dirty && !settingsSaved && (
          <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-4">
            <AlertTriangle size={13} /> Unsaved changes
          </div>
        )}
        {settingsSaved && (
          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-3 py-2 mb-4">
            <CheckCircle2 size={13} /> Settings saved!
          </div>
        )}
        {settingsError && <p className="text-xs text-red-500 mb-4">{settingsError}</p>}

        {loadingSettings ? (
          <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div>
            <SettingRow label="Site Name" description="Displayed in page titles and emails">
              <input value={settings.site_name || ''} onChange={e => set('site_name', e.target.value)} className={inputCls} placeholder="inbox Me" />
            </SettingRow>
            <SettingRow label="Support Email" description="Shown in contact/help pages">
              <input value={settings.support_email || ''} onChange={e => set('support_email', e.target.value)} className={inputCls} placeholder="support@inboxme.space" />
            </SettingRow>
            <SettingRow label="Maintenance Mode" description="When on, shows a maintenance page to all visitors">
              <div className="flex items-center gap-3">
                <Toggle checked={bool('maintenance_mode')} onChange={v => set('maintenance_mode', String(v))} />
                <span className={`text-sm font-medium ${bool('maintenance_mode') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {bool('maintenance_mode') ? '🔴 Maintenance ON' : '🟢 Site Live'}
                </span>
              </div>
            </SettingRow>
            <SettingRow label="New Inbox Registration" description="Allow visitors to create new inboxes">
              <div className="flex items-center gap-3">
                <Toggle checked={bool('registration_enabled')} onChange={v => set('registration_enabled', String(v))} />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {bool('registration_enabled') ? 'Enabled' : 'Disabled (no new inboxes)'}
                </span>
              </div>
            </SettingRow>
            <SettingRow label="Max Inbox Duration (hours)" description="How long inboxes last before auto-expiring">
              <input
                type="number" min={1} max={720}
                value={settings.max_inbox_duration_hours || '24'}
                onChange={e => set('max_inbox_duration_hours', e.target.value)}
                className={inputCls}
              />
            </SettingRow>
          </div>
        )}
      </section>

      {/* ── SMTP Settings ─────────────────────────────── */}
      <section className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} className="text-violet-500" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Outbound Email (SMTP)</h2>
          <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">For admin notifications</span>
        </div>
        {loadingSettings ? (
          <div className="py-4 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div>
            <SettingRow label="SMTP Host">
              <input value={settings.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} className={inputCls} placeholder="smtp.mailgun.org" />
            </SettingRow>
            <SettingRow label="SMTP Port">
              <input type="number" value={settings.smtp_port || '587'} onChange={e => set('smtp_port', e.target.value)} className={inputCls} placeholder="587" />
            </SettingRow>
            <SettingRow label="SMTP Username">
              <input value={settings.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} className={inputCls} placeholder="postmaster@inboxme.space" />
            </SettingRow>
            <SettingRow label="SMTP Password">
              <input type="password" autoComplete="new-password" value={settings.smtp_pass || ''} onChange={e => set('smtp_pass', e.target.value)} className={inputCls} placeholder="••••••••" />
            </SettingRow>
            <SettingRow label="From Address">
              <input value={settings.smtp_from || ''} onChange={e => set('smtp_from', e.target.value)} className={inputCls} placeholder="noreply@inboxme.space" />
            </SettingRow>
            <div className="mt-4 flex justify-end">
              <button onClick={saveSettings} disabled={settingsSaving || !dirty}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition">
                <Save size={14} /> {settingsSaving ? 'Saving…' : 'Save SMTP'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Inbox Defaults ─────────────────────────────── */}
      <section className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-emerald-500" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Inbox Defaults</h2>
        </div>
        {!loadingSettings && (
          <div>
            <SettingRow label="Max Inbox Duration" description="Hours until auto-expiry (1–720)">
              <div className="flex items-center gap-3">
                <input type="range" min={1} max={720} step={1}
                  value={settings.max_inbox_duration_hours || '24'}
                  onChange={e => set('max_inbox_duration_hours', e.target.value)}
                  className="flex-1 accent-emerald-500" />
                <span className="text-sm font-bold text-gray-700 dark:text-gray-300 w-16 text-right">
                  {settings.max_inbox_duration_hours || 24}h
                </span>
              </div>
            </SettingRow>
            <div className="mt-4 flex justify-end">
              <button onClick={saveSettings} disabled={settingsSaving || !dirty}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition">
                <Save size={14} /> {settingsSaving ? 'Saving…' : 'Save Defaults'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Change Password ─────────────────────────────── */}
      <section className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-blue-600 dark:text-blue-400" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Change Password</h2>
        </div>
        <form onSubmit={submitPwd} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Current password</label>
            <input type="password" autoComplete="current-password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} required className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">New password</label>
            <input type="password" autoComplete="new-password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required minLength={8} className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Confirm new password</label>
            <input type="password" autoComplete="new-password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required minLength={8} className={inputCls} />
          </div>
          {pwdError && <p className="text-sm text-red-600 dark:text-red-400">{pwdError}</p>}
          {pwdSuccess && <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1"><CheckCircle2 size={16} /> Password updated.</p>}
          <button type="submit" disabled={pwdSaving} className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium transition">
            {pwdSaving ? 'Saving…' : 'Update password'}
          </button>
        </form>
      </section>
    </div>
  );
}
